import { createSelector } from "@reduxjs/toolkit";
import {
  each,
  filter,
  find,
  findIndex,
  findLast,
  flatten,
  forEach,
  groupBy,
  keyBy,
  keys,
  merge,
  reduce,
  toLower,
  values,
} from "lodash";
import { point } from "@turf/helpers";
import moment from "moment";

import {
  ALLOCATED,
  ROUTE_STATUS,
  UNALLOCATED,
} from "../../consts/routeDetails";
import {
  DEFAULT_TIMEBAND,
  MATRIX_MODES,
  NO_DRIVER,
} from "../../consts/strings";
import { SORT_ORDERS, ROUTE_SORT_TYPES } from "../../consts/sorters";
import { OUTER, POSTCODE, SECTOR } from "../../consts/layers";
import { FINAL_ROUTES_TIME } from "../../consts/timeSlider";
import {
  DEFAULT_COLLECTION_TYPES,
  PLANNED,
  SAME_DAY,
} from "../../consts/collections";
import {
  BULK_PLAN_TYPE,
  CHOICE_PLAN_TYPE,
  COLLECTIONS_PLAN_TYPE,
  DEFAULT_PLAN_TYPE,
  NON_DELIVERY_PLAN_TYPES,
  OVERRIDES_PLAN_TYPE,
  PICKUP_PLAN_TYPE,
  PLAN_TYPES_IMPORTANCE_ORDER,
  ROBOTS_PLAN_TYPE,
  SMALLS_PLAN_TYPE,
} from "../../consts/planTypes";
import { ODF_DRIVER_TYPE } from "../../consts/driverTypes";

import {
  addMissingOuters,
  processRoutesInTimebandWithOverlay,
} from "../../utils/matrixProfile";
import { routesSorters } from "../../utils/sorters";
import {
  calculateTotals,
  getOverlayBeforeTimeBand,
  getLastOverlayTimeBand,
  includeStopBasedOnType,
  overlayDetailsAsObject,
} from "../../utils/volumeData";
import {
  generateColor,
  getRouteHasVolume,
  routeHasTerritory,
  routesAsObject,
  summaryForRoutes,
  getRoutesForTimeband,
  isRedirectionRouteCode,
  isNonRedirectionRouteCode,
} from "../../utils/routes";
import {
  getMismatchesForLabels,
  setCollectionRoute,
} from "../../utils/collections";
import { timeSliderFormat } from "../../utils/timeSlider";
import { getVolume } from "../../utils/volumes";
import { Postcode } from "../../utils/postcode";
import { copyOverlayDetails } from "../../utils/volumeData";
import { generateLabelsForLevel } from "../../utils/map";
import { getPostcodeAllocation } from "../../utils/postcodeAllocation";
import { getAreasWaveSplit } from "../../utils/defaultType";
import { divideToInt } from "../../utils/math";
import { getOverrideMap } from "../../utils/overrides";
import { getRedirectForPostcode } from "../../utils/redirectConfig";
import { groupPostcodes, groupSectors } from "../../utils/lookUpTables";
import { getDriverlessRoutes } from "../../utils/drivers";

import {
  centerPointSelector,
  visiblePostcodes,
  zoomLevelSelector,
} from "../map/selectors";
import {
  visibleArtificialPostcodesSelector,
  depotDefaultCollectionValuesSelector,
  lookupTableSelector,
} from "../depot/selectors";
import {
  markerViewTypeSelector,
  selectedCollectionTabSelector,
  selectedPostcodesSelector,
  selectedRouteSelector,
  selectedTimeBandSelector,
} from "../ownApp/selectors";
import { filtersSelector, filterValueSelector } from "../filters/selectors";
import { sortOrderSelector, routesSorterSelector } from "../sorters/selectors";
import { getLocationVolume } from "../../utils/locations";
import { getPickupPopupLabels, getPickupToPostcode } from "../../utils/pickup";
import { PICKUP_TYPE } from "../../consts/filters/pickupTypes";

export const userSessionExpiredSelector = (state) =>
  state.matrixProfile.userSessionExpired;
export const locksRefreshRequiredSelector = (state) =>
  state.matrixProfile.locksRefreshRequired;
export const matrixProfileSelector = (state) => state.matrixProfile;
export const isProfileSelectedSelector = (state) =>
  state.matrixProfile.profile && state.matrixProfile.type;
export const getSelectedMatrixProfile = (state) => state.matrixProfile.profile;
export const isMatrixProfileSelectedSelector = (state) =>
  !!state.matrixProfile.profile && !!state.matrixProfile.type;
export const getMatrixProfileType = (state) => state.matrixProfile.type;
export const getMatrixProfileMessage = (state) => state.matrixProfile.message;
export const getMatrixProfiles = (state) => ({
  dayProfiles: state.matrixProfile.dayProfiles,
  datedProfiles: state.matrixProfile.datedProfiles,
  templates: state.matrixProfile.templates,
});
export const getDepotDays = (state) =>
  state.matrixProfile.dayProfiles.map((profile) => parseInt(profile.day, 10));

export const getRouteMatrix = (state) => state.matrixProfile.routeMatrix;
export const getSelectedPlanDay = (state) =>
  state.matrixProfile.routeMatrix?.matrixProfile?.day;
export const getSelectedPlanDate = (state) =>
  state.matrixProfile.routeMatrix?.matrixProfile?.activeDate;
export const routeConfigEditSelector = (state) =>
  state.matrixProfile.routeConfigEdit;
export const routeConfigEditCopySelector = (state) =>
  state.matrixProfile.routeConfigEditCopy;
export const redirectConfigSelector = (state) =>
  state.matrixProfile.redirectConfig;
export const redirectConfigCopySelector = (state) =>
  state.matrixProfile.redirectConfigCopy;
export const routeViewSelector = (state) => state.matrixProfile.routeView;
export const routePostcodesSelector = (state) =>
  state.matrixProfile.routePostcodes;
export const matrixProfileLoadingSelector = (state) =>
  state.matrixProfile.loading;
export const routeLabelsSelector = (state) => state.matrixProfile.routeLabels;
export const multiPostcodesSelector = (state) =>
  state.matrixProfile.multiPostcodes;

export const overlayDetailsSelector = (state) =>
  state.matrixProfile.volumeData?.overlayDetails;
export const volumeDataSelector = (state) => state.matrixProfile.volumeData;
export const heatmapViewSelector = (state) =>
  state.matrixProfile.heatmapView &&
  !state.matrixProfile.type !== COLLECTIONS_PLAN_TYPE;
export const overlaysSelector = (state) => state.matrixProfile.overlays;
export const selectedOverlaySelector = (state) =>
  state.matrixProfile.selectedOverlay;
export const postcodeToRouteSelector = (state) =>
  state.matrixProfile.postcodeToRoute;
export const collectionsSelector = (state) =>
  state.matrixProfile.scheduledCollections;
export const collectionViewSelector = (state) =>
  state.matrixProfile.collectionsView;
export const selectedCollectionSelector = (state) =>
  state.matrixProfile.selectedCollection;
export const matrixProfileSessionChecksumSelector = (state) =>
  state.matrixProfile.sessionChecksum;
export const matrixProfileChecksumSelector = (state) =>
  state.matrixProfile.checksum;
export const matrixProfileLockSelector = (state) => ({
  lockedByYou: state.matrixProfile.lockedByYou,
  lockedInDifferentBrowser: state.matrixProfile.lockedInDifferentBrowser,
  lockedByUserCode: state.matrixProfile.lockedByUserCode,
  lockedByUserName: state.matrixProfile.lockedByUserName,
  lockedByText: state.matrixProfile.lockedByUserCode
    ? `Plan Locked By ${
        state.matrixProfile.lockedByYou
          ? `You${
              state.matrixProfile.lockedInDifferentBrowser
                ? " In Different Browser"
                : ""
            }`
          : state.matrixProfile.lockedByUserName
      }`
    : null,
  lockedDatetime:
    !state.matrixProfile.lockedInDifferentBrowser &&
    state.matrixProfile.lockedDatetime,
});
export const pickupPartnersSelector = (state) =>
  state.matrixProfile.pickupPartners;
export const vehiclesSelector = (state) => state.matrixProfile.vehicles;
export const driverInEditSelector = (state) => state.matrixProfile.driverEdit;
export const driverInEditCopySelector = (state) =>
  state.matrixProfile.driverEditCopy;
export const driverEditCodeSelector = (state) =>
  state.matrixProfile.driverEdit?.driverCode;
export const driverEditModalOpenSelector = createSelector(
  driverInEditSelector,
  (driverEdit) => !!driverEdit
);
export const driverInEditOdfCodeSelector = createSelector(
  driverInEditSelector,
  (driverEdit) => {
    return driverEdit.driverType === ODF_DRIVER_TYPE
      ? driverEdit.driverCode
      : driverEdit.plannedOdf || "";
  }
);
export const routeDayLocksSelector = (state) =>
  state.matrixProfile.routeDayLocks;
export const routeDayLockSelector = (routeCode) => (state) =>
  state.matrixProfile.routeDayLocks[routeCode];
export const anyRouteDayLockedSelector = createSelector(
  routeDayLocksSelector,
  (routeDayLocks) =>
    Object.values(routeDayLocks).some(
      ({ lockedByUserCode }) => lockedByUserCode
    )
);
export const driverProfileLocksSelector = (state) =>
  state.matrixProfile.driverProfileLocks;
export const driverProfileLockSelector = (driverCode) => (state) =>
  state.matrixProfile.driverProfileLocks[driverCode];
export const driverDayLocksSelector = (state) =>
  state.matrixProfile.driverDayLocks;
export const driverDayLockSelector = (driverCode) => (state) =>
  state.matrixProfile.driverDayLocks[driverCode];
export const anyDriverDayLockedSelector = createSelector(
  driverDayLocksSelector,
  (driverDayLocks) =>
    Object.values(driverDayLocks).some(
      ({ lockedByUserCode }) => lockedByUserCode
    )
);
export const driversSelector = (state) => state.matrixProfile.drivers;
export const driverSelector = (driverCode) => (state) =>
  state.matrixProfile.drivers?.[driverCode];
export const routeInEditSelector = (state) => state.matrixProfile.routeEdit;
export const routeInEditCodeSelector = (state) =>
  state.matrixProfile.routeEdit?.routeCode;
export const routesSelector = (state) =>
  state.matrixProfile.editRoutes || state.matrixProfile.routes;
export const editRoutesSelector = (state) => state.matrixProfile.editRoutes;
export const editRoutesCopySelector = (state) =>
  state.matrixProfile.editRoutesCopy;
export const matrixModeSelector = (state) => state.matrixProfile.matrixMode;
export const changedOuterSelector = (state) => state.matrixProfile.changedOuter;
export const driversImpactedSelector = (state) =>
  state.matrixProfile.driversImpacted;
export const routesImpactedSelector = (state) =>
  state.matrixProfile.routesImpacted;
export const routeCodesImpactedSelector = (state) =>
  Object.keys(state.matrixProfile.routesImpacted).map((routeCode) => ({
    routeCode,
  }));
export const routesModifiedSelector = (state) =>
  state.matrixProfile.routesModified;
export const routesModifiedCopySelector = (state) =>
  state.matrixProfile.routesModifiedCopy;
export const showQuestionMarksSelector = (state) =>
  state.matrixProfile.heatmapView;
export const routesByOuterSelector = (state) =>
  state.matrixProfile.routesByOuter;
export const routesTimesSelector = (state) => state.matrixProfile.routesTimes;
export const routesTimesCopySelector = (state) =>
  state.matrixProfile.routesTimesCopy;
export const cutOffTimesSelector = (state) => state.matrixProfile.cutOffTimes;
export const multiRoutesPopupLocationSelector = (state) =>
  state.matrixProfile.multiRoutesPopupLocation;
export const multiRoutePopupRoutesSelector = createSelector(
  lookupTableSelector,
  postcodeToRouteSelector,
  multiRoutesPopupLocationSelector,
  (lookupTables, postcodeToRoute, { postcode: popupPostcode } = {}) => {
    if (!lookupTables || !postcodeToRoute || !popupPostcode) return [];

    const labelPostcodes = [];

    const level = Postcode.getLevel(popupPostcode);
    labelPostcodes.push({ level, postcode: popupPostcode });
    if (level === OUTER) {
      const sectors = groupSectors(lookupTables.SECTOR)[popupPostcode];
      sectors.forEach((sector) =>
        labelPostcodes.push({ level: SECTOR, postcode: sector })
      );
      sectors.forEach((sector) =>
        groupPostcodes(lookupTables.POSTCODE)[sector].forEach((postcode) =>
          labelPostcodes.push({ level: POSTCODE, postcode })
        )
      );
    } else if (level === SECTOR) {
      const postcodes = groupPostcodes(lookupTables.POSTCODE)[popupPostcode];
      postcodes.forEach((postcode) =>
        labelPostcodes.push({ level: POSTCODE, postcode })
      );
    }

    const routeCodes = new Set();
    labelPostcodes.forEach(
      ({ level, postcode }) =>
        postcodeToRoute[level][postcode] &&
        routeCodes.add(postcodeToRoute[level][postcode])
    );
    return Array.from(routeCodes);
  }
);

export const optimiseResultsOpenSelector = (state) =>
  state.matrixProfile.optimiseResultsOpen;
export const templateIdSelector = (state) =>
  state.matrixProfile.routeMatrix?.matrixProfile?.templateId;
export const isProfileValidSelector = (state) =>
  state.matrixProfile.routeMatrix?.matrixProfile?.planValidated;
export const driverRequirementsSelector = (state) =>
  state.matrixProfile.routeMatrix?.matrixProfile?.driverRequirements;
export const isWaveEditModeSelector = (state) =>
  state.matrixProfile.waveEditMode;
export const collectionAreasSelector = (state) =>
  state.matrixProfile.editCollectionAreas ||
  state.matrixProfile.collectionAreas;
export const editCollectionAreasSelector = (state) =>
  state.matrixProfile.editCollectionAreas;
export const overrideAreasSelector = (state) =>
  state.matrixProfile.editOverrideAreas || state.matrixProfile.overrideAreas;
export const pickupAreasSelector = (state) =>
  state.matrixProfile.editPickupAreas || state.matrixProfile.pickupAreas;
export const editPickupAreasSelector = (state) =>
  state.matrixProfile.editPickupAreas;
export const pickupTypesByRouteCodeSelector = createSelector(
  pickupPartnersSelector,
  pickupAreasSelector,
  getMatrixProfileType,
  (pickupPartners, pickupAreas, planType) => {
    if (!pickupPartners || !pickupAreas || planType !== PICKUP_PLAN_TYPE)
      return null;

    const partnerTypesByRouteCode = [OUTER, SECTOR, POSTCODE].reduce(
      (acc, level) => {
        Object.values(pickupAreas[level]).forEach((pickupLevelConfig) =>
          Object.entries(pickupLevelConfig).forEach(
            ([partnerType, routeCode]) => {
              if (!acc[routeCode]) acc[routeCode] = {};
              acc[routeCode][partnerType] = true;
            }
          )
        );
        return acc;
      },
      {}
    );

    const partnerTypesByKind = pickupPartners.reduce(
      (acc, { partnerType, kind }) => {
        if (!acc[kind]) acc[kind] = [];
        acc[kind].push(partnerType);
        return acc;
      },
      {}
    );

    Object.values(partnerTypesByRouteCode).forEach((routePartnerTypes) => {
      Object.entries(partnerTypesByKind).forEach(([kind, kindPartnerTypes]) => {
        if (
          kindPartnerTypes.some(
            (kindPartnerType) => routePartnerTypes[kindPartnerType]
          )
        )
          routePartnerTypes[kind] = true;
      });
    });

    return partnerTypesByRouteCode;
  }
);
export const simulationPercentagesSelector = (state) =>
  state.matrixProfile.simulationPercentages;
export const depotRoutesOptimisedSelector = (state) =>
  state.matrixProfile.depotRoutesOptimised;
export const editModalOverridesSelector = (state) =>
  state.matrixProfile.editModalOverrides;
export const editModalPickupAreasSelector = (state) =>
  state.matrixProfile.editModalPickupAreas;
export const pickupModalConfigSelector = (state) =>
  state.matrixProfile.pickupModalConfig;
export const locationConfigSelector = (state) =>
  state.matrixProfile.editLocationConfig || state.matrixProfile.locationConfig;
export const editLocationConfigSelector = (state) =>
  state.matrixProfile.editLocationConfig;
export const locationSelector = (locationCode) =>
  createSelector(locationConfigSelector, (locationConfig) => {
    if (!locationConfig) return null;
    else
      return (
        locationConfig.find(
          (location) => location.locationCode === locationCode
        ) || null
      );
  });

export const unallocatedLocationsSelector = createSelector(
  locationConfigSelector,
  (locationConfig) => {
    if (!locationConfig) return [];
    else return locationConfig.filter(({ routeCode }) => !routeCode);
  }
);

export const noCoordinatesLocationSelector = (state) =>
  state.matrixProfile.noCoordinatesLocation;

export const getPlanCollectionDefaults = createSelector(
  getSelectedPlanDay,
  getSelectedPlanDate,
  depotDefaultCollectionValuesSelector,
  (planDay, planDate, depotDefaults) => {
    const day = planDay || moment(planDate).day();
    const {
      defaultEarliestReadyTime = [],
      defaultLatestReadyTime = [],
      defaultSameDayCutOff = [],
      defaultSaturnAuto = [],
    } = depotDefaults || {};
    return {
      defaultEarliestReadyTime: defaultEarliestReadyTime[day],
      defaultLatestReadyTime: defaultLatestReadyTime[day],
      defaultSameDayCutOff: defaultSameDayCutOff[day],
      defaultSaturnAuto: defaultSaturnAuto[day],
    };
  }
);

export const collectionAreaForPostcodeSelector = (area) =>
  createSelector(
    collectionAreasSelector,
    getPlanCollectionDefaults,
    (collectionAreas, collectionDefaults) => {
      if (!area) return;
      const postcode = new Postcode(area);
      const collection = collectionAreas[postcode.getLevel()][area];
      return {
        postcode: area,
        sameDayCutOff:
          collection?.sameDayCutOff || collectionDefaults.defaultSameDayCutOff,
        earliestReadyTime:
          collection?.earliestReadyTime ||
          collectionDefaults.defaultEarliestReadyTime,
        latestReadyTime:
          collection?.latestReadyTime ||
          collectionDefaults.defaultLatestReadyTime,
        saturnAuto:
          collection?.saturnAuto === true || collection?.saturnAuto === false
            ? collection.saturnAuto
            : collectionDefaults.defaultSaturnAuto,
      };
    }
  );

export const unallocatedVolumeSelector = ({ postcode, routeCode }) =>
  createSelector(
    postcodeToRouteSelector,
    volumesSinceLastReadyTimeSelector,
    lookupTableSelector,
    (postcodeToRoute, volumes, lookupTables) => {
      if (!!routeCode) return;
      let postcodes;
      switch (Postcode.getLevel(postcode)) {
        case OUTER:
          postcodes = groupSectors(lookupTables.SECTOR)[postcode].flatMap(
            (sector) => groupPostcodes(lookupTables.POSTCODE)[sector]
          );
          break;
        case SECTOR:
          postcodes = groupPostcodes(lookupTables.POSTCODE)[postcode];
          break;
        case POSTCODE:
          postcodes = [postcode];
          break;
        default:
          postcodes = [];
      }

      return postcodes
        .filter((postcode) => !postcodeToRoute.POSTCODE[postcode])
        .reduce(
          (acc, postcode) => {
            acc.stops += volumes[postcode].stops;
            acc.parcels += volumes[postcode].parcels;
            return acc;
          },
          { stops: 0, parcels: 0 }
        );
    }
  );
export const isTemplateSelector = (state) => {
  if (!state.matrixProfile.routeMatrix?.matrixProfile) return false;
  return (
    !state.matrixProfile.routeMatrix?.matrixProfile?.activeDate &&
    !state.matrixProfile.routeMatrix?.matrixProfile?.defaultActiveTemplate
  );
};
export const isDailyPlanSelector = (state) =>
  !!state.matrixProfile.routeMatrix?.matrixProfile?.defaultActiveTemplate;

export const wavesSelector = (state) => state.matrixProfile.waves;

export const sectionsSelector = (state) => state.matrixProfile.depotSections;

export const robotSuppliersSelector = (state) =>
  state.matrixProfile.robotSuppliers;

export const overlayAsObjectSelector = createSelector(
  overlayDetailsSelector,
  (overlayDetails) => {
    return overlayDetailsAsObject(overlayDetails);
  }
);

export const volumesSinceLastReadyTimeSelector = createSelector(
  overlayAsObjectSelector,
  selectedTimeBandSelector,
  cutOffTimesSelector,
  lookupTableSelector,
  getMatrixProfileType,
  isMatrixProfileSelectedSelector,
  volumeDataSelector,
  (
    overlayDetails,
    selectedTimeBand,
    cutOffTimes,
    lookupTables,
    planType,
    isMatrixProfileSelectedSelector,
    volume
  ) => {
    const volumeData = volume && {
      overlayProfile: { ...volume.overlayProfile },
      overlayDetails: copyOverlayDetails(volume.overlayDetails),
    };

    if (!!!overlayDetails) return;
    const timeBand =
      selectedTimeBand === DEFAULT_TIMEBAND || !selectedTimeBand
        ? FINAL_ROUTES_TIME
        : selectedTimeBand;
    const cutOffTimeBand = timeBand.replace("23:", "-23:");
    const volumeTimeBand = overlayDetails[timeBand]
      ? timeBand
      : getOverlayBeforeTimeBand(volume.overlayDetails, timeBand);

    const volumes = {};
    const lastReadyTimes = {};

    if (!!cutOffTimes)
      each(Object.keys(cutOffTimes), (postcode) => {
        lastReadyTimes[postcode] = findLast(
          cutOffTimes[postcode],
          (lastRouteTime) =>
            lastRouteTime.replace("23:", "-23:") < cutOffTimeBand
        );
      });

    const filteredOverlayDetails =
      isMatrixProfileSelectedSelector || !planType
        ? overlayDetails
        : overlayDetailsAsObject(
            calculateTotals({
              overlayData: volumeData?.overlayDetails,
              planType,
              validPostcodes: Object.keys(lookupTables?.POSTCODE || {}),
            })
          );

    each(Object.keys(lookupTables.POSTCODE), (postcode) => {
      volumes[postcode] = getVolume({
        volumeTimeBand,
        postcode,
        lastRouteTimeBand: lastReadyTimes[postcode],
        overlayDetails: filteredOverlayDetails,
        level: "overlayPostcode",
      });
    });

    const groupedBySector = groupBy(Object.keys(volumes), (i) =>
      new Postcode(i).toSector()
    );
    const sectors = {};
    each(Object.keys(groupedBySector), (sector) => {
      volumes[sector] = reduce(
        groupedBySector[sector],
        (acc, postcode) => {
          acc.stops += volumes[postcode].stops;
          acc.parcels += volumes[postcode].parcels;
          return acc;
        },
        { stops: 0, parcels: 0 }
      );
      sectors[sector] = volumes[sector];
    });

    const groupedByOuter = groupBy(Object.keys(sectors), (i) =>
      new Postcode(i).toOuter()
    );
    each(Object.keys(groupedByOuter), (outer) => {
      volumes[outer] = reduce(
        groupedByOuter[outer],
        (acc, postcode) => {
          acc.stops += volumes[postcode].stops;
          acc.parcels += volumes[postcode].parcels;
          return acc;
        },
        { stops: 0, parcels: 0 }
      );
    });

    return volumes;
  }
);

export const locationVolumesSelector = createSelector(
  overlayDetailsSelector,
  (overlayDetails) => {
    if (!overlayDetails) return null;
    else
      return overlayDetails.reduce((acc, { overlayStops }) => {
        overlayStops.forEach((stop) => {
          if (stop.locationInvalid) return;
          if (!acc[stop.locationCode]) acc[stop.locationCode] = [];
          acc[stop.locationCode].push(stop);
        });
        return acc;
      }, {});
  }
);

export const pickupVolumesSelector = createSelector(
  filterValueSelector(PICKUP_TYPE.field),
  pickupPartnersSelector,
  overlayDetailsSelector,
  (filter, pickupPartners, overlayDetails) => {
    if (!pickupPartners || !overlayDetails) return null;

    const filterValue =
      !filter || filter.value === filter.defaultValue.value
        ? null
        : filter.value;
    const partnerTypeMap = pickupPartners
      .filter(
        ({ partnerType, kind }) =>
          !filterValue || [partnerType, kind].includes(filterValue)
      )
      .reduce((acc, { partnerType }) => {
        acc[partnerType] = true;
        return acc;
      }, {});

    const pickupVolumes = {};
    const addStop = ({ routeCode, postcode, stop }) => {
      if (!pickupVolumes[routeCode][postcode])
        pickupVolumes[routeCode][postcode] = [];

      pickupVolumes[routeCode][postcode].push(stop);
    };

    overlayDetails.forEach(({ overlayStops }) => {
      overlayStops.forEach((stop) => {
        if (
          stop.stopType === OVERRIDES_PLAN_TYPE ||
          !partnerTypeMap[stop.partnerType] ||
          stop.postcodeInvalid
        )
          return;
        const postcode = new Postcode(stop.postcode);
        const sector = postcode.toSector();
        const outer = postcode.toOuter();

        const routeCode = stop.routeCode || null;
        if (!pickupVolumes[routeCode]) pickupVolumes[routeCode] = {};

        [stop.postcode, sector, outer].forEach((postcode) =>
          addStop({ routeCode, postcode, stop })
        );
      });
    }, {});
    return pickupVolumes;
  }
);

export const isEditModeSelector = createSelector(
  matrixModeSelector,
  (matrixMode) => {
    return (
      [
        MATRIX_MODES.EDIT,
        MATRIX_MODES.EDIT_ONE_ROUTE,
        MATRIX_MODES.COLLECTION_TIMES_EDIT,
      ].indexOf(matrixMode) > -1
    );
  }
);

export const isEditOneModeSelector = createSelector(
  matrixModeSelector,
  (matrixMode) => matrixMode === MATRIX_MODES.EDIT_ONE_ROUTE
);

export const routesForPlanTypeSelector = createSelector(
  routesSelector,
  getMatrixProfileType,
  (routes, planType) => {
    if (NON_DELIVERY_PLAN_TYPES.includes(planType))
      return filter(
        routes || [],
        ({ routeType }) =>
          ![CHOICE_PLAN_TYPE, ROBOTS_PLAN_TYPE].includes(routeType)
      );
    else if ([CHOICE_PLAN_TYPE, ROBOTS_PLAN_TYPE].includes(planType))
      return filter(
        routes || [],
        ({
          routeType,
          overridesAllocated,
          locationRoute,
          pickupAllocated,
          plannedRoute,
          sameDayRoute,
        }) =>
          (!!!routeType || routeType === planType) &&
          !overridesAllocated &&
          !locationRoute &&
          !pickupAllocated &&
          !plannedRoute &&
          !sameDayRoute
      );
    else
      return filter(
        routes || [],
        ({ routeType }) => !!!routeType || routeType === planType
      );
  }
);

export const nonRedirectionRoutesForPlanTypeSelector = createSelector(
  routesForPlanTypeSelector,
  (routes) =>
    filter(routes || [], ({ routeCode }) =>
      isNonRedirectionRouteCode(routeCode)
    )
);

export const sortedRouteListWithFiltersSelector = createSelector(
  vehiclesSelector,
  driversSelector,
  nonRedirectionRoutesForPlanTypeSelector,
  getMatrixProfileType,
  selectedCollectionTabSelector,
  pickupTypesByRouteCodeSelector,
  filtersSelector,
  sortOrderSelector,
  routesSorterSelector,
  isEditOneModeSelector,
  (
    vehicles,
    drivers,
    routes,
    planType,
    collectionType,
    pickupTypesByRouteCode,
    filters,
    sortOrder,
    sorter,
    isEditOneMode
  ) => {
    if (!isEditOneMode)
      Object.keys(filters).forEach((filterType) => {
        routes = routes.filter((route) => {
          const routeDriver = drivers[route.driverCode];
          const routeVehicle = vehicles[routeDriver?.vehicleType];
          return filters[filterType].filterCallback({
            route,
            driver: routeDriver,
            vehicle: routeVehicle,
            pickupTypes: pickupTypesByRouteCode,
            ...filters[filterType].functionParameters,
          });
        });
      });

    const routeNumberSorter = routesSorters[ROUTE_SORT_TYPES.ROUTE_NUMBER];
    const sortedRoutes = [...routes].sort((a, b) =>
      routeNumberSorter({ route: a }, { route: b })
    );

    if (sorter === routeNumberSorter && sortOrder === SORT_ORDERS.DESC)
      return sortedRoutes;
    else
      return sortedRoutes.sort((a, b) =>
        sorter(
          {
            route: a,
            driver: drivers[a.driverCode],
          },
          {
            route: b,
            driver: drivers[b.driverCode],
          },
          planType,
          collectionType
        )
      );
  }
);

export const areasWaveSplitSelector = createSelector(
  getMatrixProfileType,
  lookupTableSelector,
  routesSelector,
  routesTimesSelector,
  routesByOuterSelector,
  (planType, lookupTables, routes, routesTimes, routesByOuter) => {
    if (
      planType !== DEFAULT_PLAN_TYPE ||
      !lookupTables ||
      !routes ||
      !routesTimes ||
      !routesByOuter
    )
      return {
        unallocatedAreas: [],
        singleWaveAreas: [],
      };
    return getAreasWaveSplit({
      routes,
      routesTimes,
      routesByOuter,
      lookupTables,
    });
  }
);

export const driverlessRoutesSelector = createSelector(
  driverRequirementsSelector,
  routesSelector,
  getMatrixProfileType,
  (driverRequirements, routes, planType) => {
    if (!driverRequirements || !routes || !planType) return {};

    const routesObj = routes.reduce((acc, route) => {
      acc[route.routeCode] = route;
      return acc;
    }, {});

    const typesInOrder = [
      planType,
      ...PLAN_TYPES_IMPORTANCE_ORDER.filter((t) => t !== planType),
    ];
    return typesInOrder.reduce((acc, type) => {
      if (type === planType) acc[type] = getDriverlessRoutes(routes, planType);
      else
        acc[type] = driverRequirements[type]
          .filter(
            (routeCode) =>
              !routesObj[routeCode].driverCode &&
              !acc[planType].find((route) => route.routeCode === routeCode)
          )
          .map((routeCode) => routesObj[routeCode]);
      return acc;
    }, {});
  }
);

export const hasDriverlessRoutesSelector = createSelector(
  driverlessRoutesSelector,
  (driverlessRoutes) =>
    Object.values(driverlessRoutes).some(({ length }) => !!length)
);

export const scheduledCollectionsSelector = createSelector(
  collectionsSelector,
  (collections) => {
    return collections && groupBy(collections, "collectionPostcode");
  }
);

export const visibleScheduledCollectionsSelector = createSelector(
  scheduledCollectionsSelector,
  visiblePostcodes,
  visibleArtificialPostcodesSelector,
  collectionViewSelector,
  (
    collections,
    visiblePostcodes,
    visibleArtificialPostcodes,
    collectionView
  ) => {
    if (!collectionView || !collections) return [];
    const filteredCollections = [];
    Object.keys(collections).forEach((key) => {
      if (
        visiblePostcodes.some((postcode) => key.includes(postcode)) ||
        visibleArtificialPostcodes.some((postcode) => key.includes(postcode))
      ) {
        filteredCollections.push(collections[key]);
      }
    });
    return filteredCollections;
  }
);

export const postcodeAllocationSelector = createSelector(
  routesForPlanTypeSelector,
  (routes) => {
    return getPostcodeAllocation({ routes });
  }
);

export const updatedSelectedRouteSelector = createSelector(
  routesForPlanTypeSelector,
  selectedRouteSelector,
  (routes, selectedRoute) => {
    if (!routes || !selectedRoute) {
      return null;
    }
    return routes.find((route) => route.routeCode === selectedRoute.routeCode);
  }
);

export const routesInTimebandSelector = createSelector(
  selectedTimeBandSelector,
  routesForPlanTypeSelector,
  getMatrixProfileType,
  selectedCollectionTabSelector,
  (selectedTimeBand, routes, planType, collectionType) => {
    if (!routes || !selectedTimeBand)
      return { routeOuter: [], routeSector: [], routePostcode: [] };
    return getRoutesForTimeband(
      routes,
      selectedTimeBand,
      planType,
      collectionType
    );
  }
);

export const labelsForSelectedRouteSelector = createSelector(
  getMatrixProfileType,
  selectedCollectionTabSelector,
  selectedRouteSelector,
  selectedOverlaySelector,
  routePostcodesSelector,
  lookupTableSelector,
  multiPostcodesSelector,
  postcodeToRouteSelector,
  zoomLevelSelector,
  visiblePostcodes,
  visibleArtificialPostcodesSelector,
  (
    planType,
    collectionType,
    selectedRoute,
    selectedOverlay,
    routePostcodes,
    lookupTable,
    multiPostcodes,
    postcodeToRoute,
    level,
    visiblePostcodes,
    visibleArtificialPostcodes
  ) => {
    const routeLabels = [];
    if (!selectedRoute || !selectedOverlay) return routeLabels;
    generateLabelsForLevel({
      planType,
      collectionType,
      routePostcodes: {
        [selectedRoute.routeCode]:
          routePostcodes[level][selectedRoute.routeCode],
      },
      lookupTable: lookupTable[level],
      routeLabels,
      multiPostcodes: multiPostcodes[level],
      postcodeToRoute: postcodeToRoute[level],
      individual: true,
    });
    return filter(
      routeLabels,
      (label) =>
        visiblePostcodes.includes(label.properties.postcode) ||
        visibleArtificialPostcodes.includes(label.properties.postcode)
    );
  }
);

export const collectionOverridesVisibleLabelsSelector = createSelector(
  visiblePostcodes,
  visibleArtificialPostcodesSelector,
  collectionAreasSelector,
  lookupTableSelector,
  getPlanCollectionDefaults,
  getSelectedPlanDay,
  getSelectedPlanDate,
  (
    visiblePostcodes,
    visibleArtificialPostcodes,
    collectionAreas,
    lookupTable,
    collectionDefaults
  ) => {
    if (!collectionAreas || !visiblePostcodes) return;
    const level = visiblePostcodes.length
      ? new Postcode(visiblePostcodes[0]).getLevel()
      : OUTER;
    const labels = {};
    const mapPostcode = (p) => {
      const postcode = new Postcode(p);
      const postcodeForLevel = postcode.getPostcode(level);

      if (collectionAreas[level][postcodeForLevel]) {
        const collection = collectionAreas[level][postcodeForLevel];
        labels[postcodeForLevel] = {
          postcode:
            collection.postcode || collection.sector || collection.outer,
          collection: {
            earliestReadyTime:
              collection.earliestReadyTime !==
              collectionDefaults.defaultEarliestReadyTime
                ? collection.earliestReadyTime
                : null,
            latestReadyTime:
              collection.latestReadyTime !==
              collectionDefaults.defaultLatestReadyTime
                ? collection.latestReadyTime
                : null,
            sameDayCutOff:
              collection.sameDayCutOff !==
              collectionDefaults.defaultSameDayCutOff
                ? collection.sameDayCutOff
                : null,
            saturnAuto:
              collection.saturnAuto !== collectionDefaults.defaultSaturnAuto
                ? collection.saturnAuto
                : null,
          },
          geometry: {
            coordinates:
              lookupTable[level][postcode.getPostcode(level)]?.center,
          },
        };
      }
    };
    each(visiblePostcodes, mapPostcode);
    each(visibleArtificialPostcodes, mapPostcode);
    getMismatchesForLabels({
      level,
      labels,
      collectionAreas,
      visiblePostcodes,
      visibleArtificialPostcodes,
      lookupTable,
      collectionDefaults,
    });
    return values(labels);
  }
);

export const pickupToPostcodeSelector = createSelector(
  filterValueSelector(PICKUP_TYPE.field),
  lookupTableSelector,
  getMatrixProfileType,
  pickupPartnersSelector,
  pickupAreasSelector,
  (filter, lookupTables, planType, pickupPartners, pickupAreas) => {
    if (
      !lookupTables ||
      !pickupAreas ||
      !pickupPartners ||
      planType !== PICKUP_PLAN_TYPE
    )
      return;
    const filterValue =
      !filter || filter.value === filter.defaultValue.value
        ? null
        : filter.value;
    return getPickupToPostcode({
      lookupTables,
      pickupPartners: pickupPartners.filter(
        ({ partnerType, kind }) =>
          !filterValue || [partnerType, kind].includes(filterValue)
      ),
      pickupAreas,
      popupLabels: getPickupPopupLabels(pickupPartners),
    });
  }
);

export const pickupConfigForPostcodeSelector = (postcode) =>
  createSelector(pickupToPostcodeSelector, (pickupToPostcode) => {
    if (!pickupToPostcode) return;
    postcode = new Postcode(postcode);
    const level = postcode.getLevel();

    let pickupConfig;
    if (level === POSTCODE)
      pickupConfig =
        pickupToPostcode.POSTCODE[postcode.getPostcode(POSTCODE)]?.own;

    if (level === SECTOR)
      pickupConfig = pickupToPostcode.SECTOR[postcode.getPostcode(SECTOR)]?.own;
    else if (level === POSTCODE && !pickupConfig)
      pickupConfig =
        pickupToPostcode.SECTOR[postcode.getPostcode(SECTOR)]?.inherited;

    if (level === OUTER)
      pickupConfig = pickupToPostcode.OUTER[postcode.getPostcode(OUTER)]?.own;
    else if (!pickupConfig)
      pickupConfig =
        pickupToPostcode.OUTER[postcode.getPostcode(OUTER)]?.inherited;

    return (
      pickupConfig || {
        configByRouteCode: {},
        popupLabels: {},
        outerRouteCodes: [null],
        sectorRouteCodes: [],
        postcodeRouteCodes: [],
        routeCodes: [null],
      }
    );
  });

export const pickupVisibleLabelsSelector = createSelector(
  pickupToPostcodeSelector,
  visiblePostcodes,
  visibleArtificialPostcodesSelector,
  lookupTableSelector,
  zoomLevelSelector,
  (
    pickupToPostcode,
    visiblePostcodes,
    visibleArtificialPostcodes,
    lookupTable,
    currentLevel
  ) => {
    if (!pickupToPostcode || !visiblePostcodes) return;
    const level = visiblePostcodes.length
      ? new Postcode(visiblePostcodes[0]).getLevel()
      : OUTER;
    if (currentLevel !== level) return;

    const labels = {};
    const mapPostcode = (p) => {
      let pickupConfig;
      const postcode = new Postcode(p);
      const postcodeForLevel = postcode.getPostcode(level);
      if (level === POSTCODE)
        pickupConfig =
          pickupToPostcode.POSTCODE[postcode.getPostcode(POSTCODE)]?.own;

      if (level === SECTOR)
        pickupConfig =
          pickupToPostcode.SECTOR[postcode.getPostcode(SECTOR)]?.own;
      else if (level === POSTCODE && !pickupConfig)
        pickupConfig =
          pickupToPostcode.SECTOR[postcode.getPostcode(SECTOR)]?.inherited;

      if (level === OUTER)
        pickupConfig = pickupToPostcode.OUTER[postcode.getPostcode(OUTER)]?.own;
      else if (!pickupConfig)
        pickupConfig =
          pickupToPostcode.OUTER[postcode.getPostcode(OUTER)]?.inherited;

      labels[postcodeForLevel] = {
        postcode: postcodeForLevel,
        geometry: {
          coordinates: lookupTable[level][postcodeForLevel]?.center,
        },
        pickupConfig: pickupConfig || {
          configByRouteCode: {},
          popupLabels: {},
          outerRouteCodes: [null],
          sectorRouteCodes: [],
          postcodeRouteCodes: [],
          routeCodes: [null],
        },
      };
    };

    each(visiblePostcodes, mapPostcode);
    each(visibleArtificialPostcodes, mapPostcode);

    const labelsArray = values(labels);
    return labelsArray;
  }
);

export const overridesVisibleLabelsSelector = createSelector(
  visiblePostcodes,
  visibleArtificialPostcodesSelector,
  overrideAreasSelector,
  lookupTableSelector,
  zoomLevelSelector,
  (
    visiblePostcodes,
    visibleArtificialPostcodes,
    overrideAreas,
    lookupTable,
    currentLevel
  ) => {
    if (!overrideAreas || !visiblePostcodes) return;
    const level = visiblePostcodes.length
      ? new Postcode(visiblePostcodes[0]).getLevel()
      : OUTER;
    if (currentLevel !== level) return;

    const calulcateLabelsForLevel = (areaType, postcodeKey, labels) => {
      const labelCopy = { ...labels };
      const mapPostcode = (p) => {
        const postcode = new Postcode(p);
        const postcodeForLevel = postcode.getPostcode(level);
        const override = overrideAreas[areaType].filter((area) => {
          let levelledAreaPostcode = new Postcode(area[postcodeKey]);
          levelledAreaPostcode = levelledAreaPostcode.getPostcode(level);
          return levelledAreaPostcode === postcodeForLevel;
        });
        if (override.length > 0) {
          const existingOverrides = labelCopy[postcodeForLevel]?.override;
          if (existingOverrides) {
            labelCopy[postcodeForLevel] = {
              postcode: postcodeForLevel,
              geometry: {
                coordinates:
                  lookupTable[level][postcode.getPostcode(level)]?.center,
              },
              override: [...existingOverrides, ...override],
            };
          } else {
            labelCopy[postcodeForLevel] = {
              postcode: postcodeForLevel,
              geometry: {
                coordinates:
                  lookupTable[level][postcode.getPostcode(level)]?.center,
              },
              override: [...override],
            };
          }
        }
      };
      each(visiblePostcodes, mapPostcode);
      each(visibleArtificialPostcodes, mapPostcode);
      return labelCopy;
    };

    let labels = {};
    switch (level) {
      case "POSTCODE":
        labels = calulcateLabelsForLevel("postcode", "postcode", labels);
        break;
      case "SECTOR":
        labels = calulcateLabelsForLevel("sector", "sector", labels);
        labels = calulcateLabelsForLevel("postcode", "postcode", labels);
        break;
      case "OUTER":
      default:
        labels = calulcateLabelsForLevel("outer", "outer", labels);
        labels = calulcateLabelsForLevel("sector", "sector", labels);
        labels = calulcateLabelsForLevel("postcode", "postcode", labels);
        break;
    }
    const labelsArray = values(labels);
    return labelsArray;
  }
);

export const visibleLabelsSelector = createSelector(
  getMatrixProfileType,
  selectedCollectionTabSelector,
  visiblePostcodes,
  visibleArtificialPostcodesSelector,
  zoomLevelSelector,
  selectedRouteSelector,
  selectedOverlaySelector,
  labelsForSelectedRouteSelector,
  heatmapViewSelector,
  lookupTableSelector,
  multiPostcodesSelector,
  postcodeToRouteSelector,
  routesForPlanTypeSelector,
  selectedTimeBandSelector,
  centerPointSelector,
  collectionOverridesVisibleLabelsSelector,
  overridesVisibleLabelsSelector,
  pickupVisibleLabelsSelector,
  (
    planType,
    collectionType,
    visiblePostcodes,
    visibleArtificialPostcodes,
    level,
    selectedRoute,
    selectedOverlay,
    labelsForSelectedRoute,
    heatmapView,
    lookupTables,
    multiPostcodes,
    postcodeToRoute,
    routes,
    selectedTimeBand,
    centerPoint,
    collectionOverridesLabels,
    overridesLabels,
    pickupLabels
  ) => {
    if (!!!lookupTables || !!!visiblePostcodes || !!!postcodeToRoute) return;
    else if (planType === OVERRIDES_PLAN_TYPE) return overridesLabels;
    else if (planType === PICKUP_PLAN_TYPE) return pickupLabels;

    const levelFromVisiblePostcodes = visiblePostcodes.length
      ? new Postcode(visiblePostcodes[0]).getLevel()
      : OUTER;
    if (levelFromVisiblePostcodes !== level) return;

    const routePostcodes = {};
    const visiblePostcodeToRoute = {};
    const mapPostcodes = (postcode) => {
      const routeFromPostcode = postcodeToRoute[level]?.[postcode];
      const routeAllocation = routePostcodes[routeFromPostcode] ?? [];
      routeAllocation.push(postcode);
      routePostcodes[routeFromPostcode] = routeAllocation;
      visiblePostcodeToRoute[postcode] = postcodeToRoute?.[level][postcode];
    };
    visiblePostcodes.forEach(mapPostcodes);
    visibleArtificialPostcodes.forEach(mapPostcodes);

    const features = [];
    generateLabelsForLevel({
      routePostcodes,
      lookupTable: lookupTables?.[level],
      routeLabels: features,
      multiPostcodes: multiPostcodes?.[level],
      postcodeToRoute: visiblePostcodeToRoute,
      individual: heatmapView,
      routes,
      planType,
      collectionType,
      selectedTimeBand,
      centerPoint,
    });

    if (planType === COLLECTIONS_PLAN_TYPE) {
      const featuresObject = reduce(
        features,
        (prev, next) => {
          prev[next.properties.postcode] = next;
          return prev;
        },
        {}
      );
      const collectionsObject = reduce(
        collectionOverridesLabels,
        (prev, next) => {
          if (!keys(multiPostcodes?.[level] ?? {}).includes(next.postcode)) {
            prev[next.postcode] = next;
          }
          return prev;
        },
        {}
      );
      return values(merge(featuresObject, collectionsObject));
    }

    if (selectedOverlay && !heatmapView)
      return [
        ...features,
        ...labelsForSelectedRoute.filter(
          (label) =>
            !!getRouteHasVolume({
              route: selectedRoute,
              postcode: label.properties.postcode,
              zoomLevel: level,
              selectedTimeBand,
            })
        ),
      ];

    if (heatmapView) {
      const postcodes = features.map((feature) => feature.properties.postcode);
      const noRouteFeatures = [];
      forEach(Object.keys(lookupTables[level]), (postcode) => {
        if (
          postcodes.includes(postcode) ||
          !visiblePostcodes.includes(postcode) ||
          !lookupTables[level][postcode].center
        )
          return;
        noRouteFeatures.push(
          point(lookupTables[level][postcode].center, { postcode })
        );
      });
      const allFeatures = [
        ...features,
        ...noRouteFeatures,
        ...labelsForSelectedRoute,
      ];
      return multiPostcodes[level]
        ? allFeatures.filter(
            (feature) =>
              !Object.keys(multiPostcodes[level]).includes(
                feature.properties.postcode
              )
          )
        : allFeatures;
    }

    return features;
  }
);

export const routeObjectsSelector = createSelector(
  routesForPlanTypeSelector,
  (routes) => {
    return routesAsObject(routes);
  }
);

export const routeSelector = (routeCode) => {
  return createSelector(routeObjectsSelector, (routes) => {
    return routes[routeCode];
  });
};

export const collectionsRouteConfigSelector = createSelector(
  collectionAreasSelector,
  lookupTableSelector,
  selectedCollectionTabSelector,
  markerViewTypeSelector,
  (collectionAreas, lookupTables, selectedTab, markerViewType) => {
    if (!collectionAreas) return;
    const fullCollectionAreas = addMissingOuters({
      routeConfig: {
        routeOuter: setCollectionRoute({
          collectionAreasForLevel: collectionAreas.OUTER,
          collectionType: selectedTab,
          defaults: !!markerViewType,
        }),
        routeSector: setCollectionRoute({
          collectionAreasForLevel: collectionAreas.SECTOR,
          collectionType: selectedTab,
          defaults: !!markerViewType,
        }),
        routePostcode: setCollectionRoute({
          collectionAreasForLevel: collectionAreas.POSTCODE,
          collectionType: selectedTab,
          defaults: !!markerViewType,
        }),
      },
      lookupTables,
      allLevels: true,
    });
    return {
      outer: values(fullCollectionAreas.routeOuter),
      sector: values(fullCollectionAreas.routeSector),
      postcode: values(fullCollectionAreas.routePostcode),
    };
  }
);

export const selectedPostcodesRouteConfigSelector = createSelector(
  selectedPostcodesSelector,
  routeInEditSelector,
  (selectedPostcodes, route) => {
    const config = { outer: [], sector: [], postcode: [] };

    each(selectedPostcodes, (p) => {
      const postcode = new Postcode(p);
      const level = toLower(postcode.getLevel());
      config[level].push({ [level]: p, routeCode: route.routeCode });
    });

    return config;
  }
);

export const routeConfigSelector = createSelector(
  selectedTimeBandSelector,
  routesInTimebandSelector,
  overlayDetailsSelector,
  heatmapViewSelector,
  postcodeAllocationSelector,
  routeObjectsSelector,
  lookupTableSelector,
  isMatrixProfileSelectedSelector,
  getMatrixProfileType,
  collectionsRouteConfigSelector,
  selectedPostcodesRouteConfigSelector,
  (
    selectedTimeBand,
    routesInTimeBand,
    overlayDetails,
    heatmap,
    postcodeAllocation,
    routesObject,
    lookupTables,
    isMatrixProfileSelected,
    matrixType,
    collectionsRouteConfig,
    selectedPostcodesRouteConfig
  ) => {
    if (!selectedTimeBand || !routesInTimeBand) return;

    if (heatmap) {
      if (!overlayDetails) return;
      else if (selectedTimeBand === DEFAULT_TIMEBAND)
        selectedTimeBand = getLastOverlayTimeBand(overlayDetails);

      let overlay = overlayDetails.find(
        ({ timeBand }) => timeBand === selectedTimeBand
      );
      if (!overlay) {
        selectedTimeBand = getOverlayBeforeTimeBand(
          overlayDetails,
          selectedTimeBand
        );
        overlay = overlayDetails.find(
          ({ timeBand }) => timeBand === selectedTimeBand
        );
      }

      return (
        overlay &&
        processRoutesInTimebandWithOverlay({
          routesInTimeBand,
          overlay,
          postcodeAllocation,
          routesObject,
          selectedTimeBand,
          overlayDetails,
        })
      );
    }

    if ([OVERRIDES_PLAN_TYPE, PICKUP_PLAN_TYPE].includes(matrixType))
      return selectedPostcodesRouteConfig;
    else if (matrixType === COLLECTIONS_PLAN_TYPE)
      return collectionsRouteConfig;

    if (isMatrixProfileSelected) {
      routesInTimeBand = addMissingOuters({
        routeConfig: routesInTimeBand,
        lookupTables,
        allLevels: matrixType === COLLECTIONS_PLAN_TYPE,
      });
    }

    return {
      outer: Object.values(routesInTimeBand.routeOuter),
      sector: Object.values(routesInTimeBand.routeSector),
      postcode: Object.values(routesInTimeBand.routePostcode),
    };
  }
);

export const routeConfigObjectLevelSelector = createSelector(
  routeConfigSelector,
  zoomLevelSelector,
  (routeConfig, level) => {
    if (!routeConfig) return;
    const config = routeConfig[level.toLowerCase()];
    if (!config || !Array.isArray(config)) return;

    return config.reduce((prev, curr) => {
      prev[curr[level.toLowerCase()]] = curr;
      return prev;
    }, {});
  }
);

export const allRoutesPostcodesSelector = createSelector(
  routePostcodesSelector,
  (routePostcode) => {
    return {
      OUTER: flatten(Object.values(routePostcode?.OUTER || [])),
      SECTOR: flatten(Object.values(routePostcode?.SECTOR || [])),
      POSTCODE: flatten(Object.values(routePostcode?.POSTCODE || [])),
    };
  }
);

export const routesListSelector = createSelector(
  getMatrixProfileType,
  selectedCollectionTabSelector,
  routeViewSelector,
  sortedRouteListWithFiltersSelector,
  (planType, collectionType, view, routes) => {
    switch (view) {
      case ALLOCATED:
        return routes.filter((route) =>
          routeHasTerritory(route, planType, collectionType)
        );
      case UNALLOCATED:
        return routes.filter(
          (route) => !routeHasTerritory(route, planType, collectionType)
        );
      default:
        return routes;
    }
  }
);

export const reorderedRoutesListSelector = createSelector(
  routesListSelector,
  routesImpactedSelector,
  routeInEditSelector,
  (routes, routesImpacted, routeEdit) => {
    if (!routeEdit || !Object.keys(routesImpacted).length) return routes;

    const routesAffected = [],
      otherRoutes = [];
    routes.forEach((route) => {
      if (
        routesImpacted[route.routeCode] &&
        route.routeCode !== routeEdit.routeCode
      ) {
        routesAffected.push(route);
      } else {
        otherRoutes.push(route);
      }
    });

    const routeEditIndex = findIndex(
      otherRoutes,
      (route) => route.routeCode === routeEdit.routeCode
    );

    return otherRoutes
      .slice(0, routeEditIndex + 1)
      .concat(routesAffected)
      .concat(otherRoutes.slice(routeEditIndex + 1, otherRoutes.length));
  }
);

export const getEarliestPlannedTime = createSelector(
  routeConfigEditSelector,
  (routeConfig) => {
    if (!routeConfig) return null;

    const firstBand = find(
      routeConfig,
      (item) =>
        item.plannedReadyTimeBand &&
        item.plannedReadyTimeBand !== DEFAULT_TIMEBAND
    );

    return firstBand ? timeSliderFormat(firstBand.plannedReadyTimeBand) : null;
  }
);

export const getRegularRouteMarkersByTimeBand = createSelector(
  getMatrixProfileType,
  selectedCollectionTabSelector,
  routesForPlanTypeSelector,
  (planType, collectionType, routes) => {
    if (!routes) return null;

    let routeMarkers = {};
    each(routes, (route) => {
      if (
        !isNonRedirectionRouteCode(route.routeCode) ||
        !routeHasTerritory(route, planType, collectionType)
      )
        return;

      let isFinal = !route.plannedReadyTime;
      let timeband = isFinal
        ? "final"
        : timeSliderFormat(route.plannedReadyTime);
      routeMarkers[timeband] = routeMarkers[timeband] || [];
      routeMarkers[timeband].push(route.routeCode);
    });

    return routeMarkers;
  }
);

export const getRegularRoutersByTimeBands = createSelector(
  routesForPlanTypeSelector,
  getRegularRouteMarkersByTimeBand,
  (routes, routeMarkers) => {
    let routesByTimeBand = {};
    const routesObject = keyBy(routes, "routeCode");
    each(routeMarkers, (routeCodes, key) => {
      routesByTimeBand[key] = routeCodes.map((routeCode) => {
        return {
          routeCode,
          ...routesObject[routeCode],
        };
      });
    });

    return routesByTimeBand;
  }
);

export const multiPostcodeFeaturesSelector = createSelector(
  lookupTableSelector,
  multiPostcodesSelector,
  (lookupTables, multiPostcodes) => {
    const multiFeatures = { OUTER: {}, SECTOR: {} };
    if (!multiPostcodes || !lookupTables) return multiFeatures;

    Object.keys(multiPostcodes.OUTER).forEach((key) => {
      if (!!!lookupTables.OUTER[key]) return;
      multiFeatures.OUTER[key] = {
        routes: multiPostcodes.OUTER[key],
        ...lookupTables.OUTER[key],
      };
    });

    Object.keys(multiPostcodes.SECTOR).forEach((key) => {
      if (!!!lookupTables.SECTOR[key]) return;
      multiFeatures.SECTOR[key] = {
        routes: multiPostcodes.SECTOR[key],
        ...lookupTables.SECTOR[key],
      };
    });
    return multiFeatures;
  }
);

export const visibleMultiPostcodeFeaturesSelector = createSelector(
  multiPostcodeFeaturesSelector,
  zoomLevelSelector,
  visiblePostcodes,
  visibleArtificialPostcodesSelector,
  getMatrixProfileType,
  collectionOverridesVisibleLabelsSelector,
  (
    multiFeatures,
    level,
    visiblePostcodes,
    visibleArtificialPostcodes,
    profileType,
    collectionOverridesLabels
  ) => {
    if (!level || level === POSTCODE) return [];
    const mappedPostcodes = visiblePostcodes
      .map((postcode) => ({ ...multiFeatures[level][postcode], postcode }))
      .filter((i) => i.routes);
    visibleArtificialPostcodes.forEach((postcode) => {
      if (multiFeatures[level][postcode]?.routes)
        mappedPostcodes.push({ ...multiFeatures[level][postcode], postcode });
    });
    const multiLabels = reduce(
      mappedPostcodes,
      (prev, next) => {
        prev[next.postcode] = next;
        return prev;
      },
      {}
    );
    const collections =
      profileType === COLLECTIONS_PLAN_TYPE
        ? reduce(
            (collectionOverridesLabels || []).filter((label) =>
              keys(multiLabels).includes(label.postcode)
            ),
            (prev, next) => {
              prev[next.postcode] = next;
              return prev;
            },
            {}
          )
        : {};
    return values(merge(multiLabels, collections));
  }
);

export const routesAffectedByEditSelector = createSelector(
  editRoutesSelector,
  routesImpactedSelector,
  (editRoutes, routesImpacted) => {
    let allAffected = [],
      notOptimised = [];

    (editRoutes || []).forEach((route) => {
      if (isRedirectionRouteCode(route.routeCode)) return;

      if (routesImpacted[route.routeCode]) {
        allAffected.push(route);
      }

      if (
        routesImpacted[route.routeCode] &&
        !routesImpacted[route.routeCode].optimised
      ) {
        notOptimised.push(route);
      }
    });

    return {
      allAffected,
      notOptimised,
    };
  }
);

export const depotSectionsSelector = createSelector(
  getMatrixProfileType,
  selectedCollectionTabSelector,
  sectionsSelector,
  routesForPlanTypeSelector,
  driversSelector,
  (planType, collectionType, sections, routes, drivers) => {
    if (!sections || !routes || !drivers) return null;
    else
      return sections.map((section, index) => {
        const { allocated, unallocated } = routes.reduce(
          (acc, route) => {
            const { sectionCode: driverSectionCode } =
              drivers[route.driverCode] || {};
            if (driverSectionCode !== section.sectionCode) return acc;
            else if (routeHasTerritory(route, planType, collectionType))
              acc.allocated.push(route);
            else acc.unallocated.push(route);
            return acc;
          },
          { allocated: [], unallocated: [] }
        );

        return {
          ...section,
          ...summaryForRoutes(allocated, drivers, planType, collectionType),
          colour: generateColor(index),
          totalRoutes: allocated.length + unallocated.length,
          totalUnallocated: unallocated.length,
        };
      });
  }
);

export const wavesListSelector = createSelector(
  getMatrixProfileType,
  selectedCollectionTabSelector,
  routesForPlanTypeSelector,
  driversSelector,
  (planType, collectionType, routes, drivers) => {
    if (!routes) return null;
    else
      return summaryForRoutes(routes, drivers, planType, collectionType).waves;
  }
);

export const locationListSelector = createSelector(
  locationConfigSelector,
  routesSelector,
  driversSelector,
  locationVolumesSelector,
  (locationConfig, routes, drivers, volumes) => {
    if (!locationConfig || !drivers) return null;
    const routesByRouteCode = routes.reduce((acc, route) => {
      acc[route.routeCode] = route;
      return acc;
    }, {});
    const driversByRouteCode = Object.values(drivers).reduce(
      (acc, driver, index) => {
        if (driver.routeCode)
          acc[driver.routeCode] = { ...driver, ...generateColor(index) };
        return acc;
      },
      {}
    );

    return locationConfig.map((location) => ({
      location,
      route: routesByRouteCode[location.routeCode] || null,
      driver: driversByRouteCode[location.routeCode] || null,
      volume: volumes
        ? getLocationVolume({ locationCode: location.locationCode, volumes })
        : null,
    }));
  }
);

export const dutyAndOpportunityRoutesSelector = createSelector(
  routesForPlanTypeSelector,
  (routes) => {
    if (!routes) return null;

    return {
      duty: routes.filter(
        (route) =>
          route.routeOptimisation?.routeStatus === ROUTE_STATUS.DUTY_EXCEEDED
      ),
      opportunity: routes.filter(
        (route) =>
          route.routeOptimisation?.routeStatus === ROUTE_STATUS.OPPORTUNITY
      ),
    };
  }
);

export const depotSummaryInformation = createSelector(
  routesForPlanTypeSelector,
  driversSelector,
  collectionsSelector,
  overrideAreasSelector,
  volumeDataSelector,
  selectedTimeBandSelector,
  getMatrixProfileType,
  selectedCollectionTabSelector,
  (
    routes,
    drivers,
    scheduledCollections,
    overrideAreas,
    volumeData,
    selectedTimeband,
    planType,
    collectionType
  ) => {
    if (!routes) return null;

    const timeBandRoutes = routes.filter(
      (route) =>
        routeHasTerritory(route, planType, collectionType) &&
        isNonRedirectionRouteCode(route.routeCode) &&
        (NON_DELIVERY_PLAN_TYPES.includes(planType) ||
          (route.plannedReadyTime === null && selectedTimeband === "default") ||
          route.plannedReadyTimeAsString === selectedTimeband)
    );

    const timeBandFigures = {
      totalStops: 0,
      totalParcels: 0,
      totalAllocated: timeBandRoutes.length,
    };

    let totalStops = 0,
      totalParcels = 0;
    (volumeData?.overlayDetails || []).forEach((timebandDetails) => {
      const isWithinSelectedTimeband =
        timebandDetails.timeBand.replace("23:", "-23:") <=
        selectedTimeband.replace("23:", "-23:");
      (timebandDetails.overlayStops || []).forEach((stop) => {
        if (
          (stop.locationInvalid && stop.postcodeInvalid) ||
          (!NON_DELIVERY_PLAN_TYPES.includes(stop.stopType) &&
            !includeStopBasedOnType({ planType, stop }))
        )
          return;

        totalStops++;
        totalParcels += stop.stopParcels || 0;

        if (!isWithinSelectedTimeband) return;
        timeBandFigures.totalStops++;
        timeBandFigures.totalParcels += stop.stopParcels || 0;
      });
    });

    timeBandFigures.stopsPercentage = divideToInt(
      timeBandFigures.totalStops * 100,
      totalStops
    );
    timeBandFigures.parcelsPercentage = divideToInt(
      timeBandFigures.totalParcels * 100,
      totalParcels
    );

    const depotRoutesSummary = summaryForRoutes(
      routes,
      drivers,
      planType,
      collectionType
    );
    const totalRoutes = routes.filter(({ routeCode }) =>
      isNonRedirectionRouteCode(routeCode)
    ).length;
    return {
      totalRoutes,
      totalCollections: Object.keys(scheduledCollections || {}).length,
      totalOverrides: Object.keys(overrideAreas || {}).reduce(
        (acc, areaType) => (acc += overrideAreas[areaType].length),
        0
      ),
      totalAllocated: depotRoutesSummary.totalRoutesAllocated,
      totalUnplanned: totalRoutes - depotRoutesSummary.totalRoutesAllocated,
      plannedStops: depotRoutesSummary.totalStops,
      plannedParcels: depotRoutesSummary.totalParcels,
      spr: depotRoutesSummary.totalSPR,
      totalParcels,
      totalStops,
      totalAverageOpportunityMinutes:
        depotRoutesSummary.totalAverageOpportunityMinutes,
      totalAverageDutyMinutes: depotRoutesSummary.totalAverageDutyMinutes,
      totalAverageDutyTime: depotRoutesSummary.totalAverageDutyTime,
      routesWithExceptions: depotRoutesSummary.routesWithExceptions,
      totalExceptions: depotRoutesSummary.totalExceptions,
      timeBandFigures,
    };
  }
);

export const volumeOverlayTableDataSelector = createSelector(
  volumeDataSelector,
  lookupTableSelector,
  (volumeOverlay, lookupTables) => {
    if (!volumeOverlay || !lookupTables)
      return {
        volumeData: [],
      };

    const validPostcodes = Object.keys(lookupTables.POSTCODE);
    let smallsCumulative = 0;
    let bulkCumulative = 0;
    let defaultCumulative = 0;
    const allOuterDetails = [];

    const volumeData = volumeOverlay.overlayDetails.map((detail, index) => {
      let cumulativeTotal = 0;
      let cadTotals = 0;
      let smallsTotals = 0;
      let bulkTotals = 0;
      let defaultTotals = 0;
      let stopsNullPostcode = 0;
      let invalidPostcode = 0;
      const outerDetails = {};

      const previousOuterDetails = index > 0 ? allOuterDetails[index - 1] : {};

      const cumulativeEmptyFigures = {
        smallsCumulative: 0,
        bulkCumulative: 0,
        defaultCumulative: 0,
        cumulativeTotal: 0,
      };

      const nonCumulativeEmptyFigures = {
        cadTotals: 0,
        smallsTotals: 0,
        bulkTotals: 0,
        defaultTotals: 0,
        stopsNullPostcode: 0,
        invalidPostcode: 0,
      };

      Object.keys(previousOuterDetails).forEach((key) => {
        outerDetails[key] = {
          ...cumulativeEmptyFigures,
          ...previousOuterDetails[key],
          ...nonCumulativeEmptyFigures,
        };

        Object.keys(outerDetails[key].sectors).forEach((sector) => {
          outerDetails[key].sectors[sector] = {
            ...cumulativeEmptyFigures,
            ...outerDetails[key].sectors[sector],
            ...nonCumulativeEmptyFigures,
          };

          Object.keys(outerDetails[key].sectors[sector].postcodes).forEach(
            (postcode) => {
              outerDetails[key].sectors[sector].postcodes[postcode] = {
                ...cumulativeEmptyFigures,
                ...outerDetails[key].sectors[sector].postcodes[postcode],
                ...nonCumulativeEmptyFigures,
              };
            }
          );
        });
      });

      Object.values(detail?.overlayOuter).forEach((outer) => {
        cumulativeTotal += outer.stopsCumulativeTotal;
        cadTotals += outer.stopsCadTotal;
      });
      detail.overlayStops.forEach((stop) => {
        if (!stop.postcode) {
          stopsNullPostcode += 1;
          return;
        }
        const postcode = new Postcode(stop.postcode);

        const emptyFiguresObj = {
          invalidPostcode: 0,
          stopsNullPostcode: 0,
          defaultTotals: 0,
          smallsTotals: 0,
          bulkTotals: 0,
          cadTotals: 0,
        };

        const outerDetail = outerDetails[postcode.toOuter()] ?? {
          outer: postcode.toOuter(),
          ...emptyFiguresObj,
          sectors: {},
        };

        const sectorDetails = outerDetail.sectors[postcode.toSector()] ?? {
          sector: postcode.toSector(),
          ...emptyFiguresObj,
          postcodes: {},
        };

        const postcodeDetails = sectorDetails.postcodes[
          postcode.toPostcode()
        ] ?? {
          postcode: postcode.toPostcode(),
          ...emptyFiguresObj,
          postcodes: {},
        };

        const appendDetails = () => {
          outerDetail.sectors[postcode.toSector()] = sectorDetails;
          outerDetail.sectors[postcode.toSector()].postcodes[
            postcode.toPostcode()
          ] = postcodeDetails;
          outerDetails[postcode.toOuter()] = outerDetail;
        };

        const incrementDetailFigures = (detailObjects, figureKeys) => {
          detailObjects.forEach((detailObject) => {
            figureKeys.forEach((figureKey) => {
              detailObject[figureKey] = (detailObject[figureKey] || 0) + 1;
            });
          });
        };

        if (!validPostcodes.includes(postcode.toPostcode())) {
          invalidPostcode += 1;
          incrementDetailFigures(
            [outerDetail, sectorDetails, postcodeDetails],
            ["invalidPostcode"]
          );
          return appendDetails();
        }

        incrementDetailFigures(
          [outerDetail, sectorDetails, postcodeDetails],
          ["cadTotals", "cumulativeTotals"]
        );

        if (stop.stopType === SMALLS_PLAN_TYPE) {
          smallsTotals += 1;
          smallsCumulative += 1;
          incrementDetailFigures(
            [outerDetail, sectorDetails, postcodeDetails],
            ["smallsTotals", "smallsCumulative"]
          );
          return appendDetails();
        }

        if (stop.stopType === BULK_PLAN_TYPE) {
          bulkTotals += 1;
          bulkCumulative += 1;
          incrementDetailFigures(
            [outerDetail, sectorDetails, postcodeDetails],
            ["bulkTotals", "bulkCumulative"]
          );
          return appendDetails();
        }

        if (
          stop.stopType === DEFAULT_PLAN_TYPE ||
          stop.stopType === COLLECTIONS_PLAN_TYPE
        ) {
          defaultTotals += 1;
          defaultCumulative += 1;
          incrementDetailFigures(
            [outerDetail, sectorDetails, postcodeDetails],
            ["defaultTotals", "defaultCumulative"]
          );
          return appendDetails();
        }
      });

      allOuterDetails.push(outerDetails);
      return {
        id: index,
        timeband: detail.timeBand,
        cadTotals: cadTotals,
        cumulativeTotals: cumulativeTotal,
        smallsTotals,
        smallsCumulative,
        bulkTotals,
        bulkCumulative,
        defaultTotals,
        defaultCumulative,
        stopsNullPostcode,
        invalidPostcode,
        outerDetails: Object.keys(outerDetails)
          .sort()
          .map((outer) => {
            return {
              ...outerDetails[outer],
              sectors: Object.keys(outerDetails[outer].sectors)
                .sort()
                .map((sector) => {
                  return {
                    ...outerDetails[outer].sectors[sector],
                    postcodes: Object.keys(
                      outerDetails[outer].sectors[sector].postcodes
                    )
                      .sort()
                      .map((postcode) => {
                        return {
                          ...outerDetails[outer].sectors[sector].postcodes[
                            postcode
                          ],
                        };
                      }),
                  };
                }),
            };
          }),
      };
    });

    return {
      volumeData,
      volumeDate: volumeOverlay.overlayProfile.overlay_date,
    };
  }
);

export const flatOverridesForEditRouteSelector = createSelector(
  overrideAreasSelector,
  routeInEditSelector,
  (overrideAreas, routeInEdit) => {
    if (!overrideAreas || !routeInEdit) return null;
    const overrides = [];

    const processOverrides = ({ overrideConfig, level = OUTER }) => {
      each(overrideConfig, (config) => {
        if (config.routeCode !== routeInEdit.routeCode) return;
        overrides.push({
          ...config,
          level,
        });
      });
    };

    processOverrides({
      overrideConfig: overrideAreas.outer,
      level: OUTER,
    });
    processOverrides({
      overrideConfig: overrideAreas.sector,
      level: SECTOR,
    });
    processOverrides({
      overrideConfig: overrideAreas.postcode,
      level: POSTCODE,
    });

    return overrides;
  }
);

export const overridesForSelectedPostcodesSelector = createSelector(
  flatOverridesForEditRouteSelector,
  selectedPostcodesSelector,
  (overrides, selectedPostcodes) => {
    if (!overrides || !selectedPostcodes.length) return null;
    const o = new Set();
    const level = Postcode.getLevel(selectedPostcodes[0]);

    let grouped = null;
    if (level === OUTER) {
      grouped = groupBy(overrides, (o) =>
        Postcode.toOuter(o.postcode || o.sector || o.outer)
      );
    } else if (level === SECTOR) {
      grouped = groupBy(overrides, (o) =>
        Postcode.toSector(o.postcode || o.sector || o.outer).replace(/ _/g, "")
      );
    } else {
      grouped = groupBy(overrides, (o) => o.postcode || o.sector || o.outer);
    }

    each(selectedPostcodes, (postcode) => {
      grouped[postcode] && o.add(grouped[postcode]);
      if (level !== OUTER) {
        const p = new Postcode(postcode);

        if (level === POSTCODE) {
          grouped[p.toSector()] && o.add(grouped[p.toSector()]);
          grouped[p.toOuter()] && o.add(grouped[p.toOuter()]);
        } else if (level === SECTOR) {
          grouped[p.toOuter()] && o.add(grouped[p.toOuter()]);
        }
      }
    });

    const flatOverrides = flatten([...o]);

    const sortOrder = {
      OUTER: 0,
      SECTOR: 1,
      POSTCODE: 2,
    };

    return flatOverrides.sort((first, second) => {
      return sortOrder[first.level] - sortOrder[second.level];
    });
  }
);

export const postcodesOptionsSelector = createSelector(
  lookupTableSelector,
  getMatrixProfileType,
  collectionAreasSelector,
  overrideAreasSelector,
  pickupToPostcodeSelector,
  routesInTimebandSelector,
  redirectConfigSelector,
  (
    lookupTables,
    planType,
    collectionAreas,
    overrideAreas,
    pickupToPostcode,
    routesInTimeBand,
    redirectConfig
  ) => {
    if (
      !lookupTables?.OUTER ||
      !lookupTables?.SECTOR ||
      !lookupTables?.POSTCODE
    )
      return [];

    const { routeOuter, routeSector, routePostcode } = routesInTimeBand;
    const overridesMap =
      planType === OVERRIDES_PLAN_TYPE ? getOverrideMap(overrideAreas) : {};

    const getPostcodePickupConfig = ({ outer, sector, postcode }) => {
      if (planType !== PICKUP_PLAN_TYPE || !pickupToPostcode) return null;
      let pickupConfig;
      if (postcode) pickupConfig = pickupToPostcode.POSTCODE[postcode]?.own;

      if (!postcode && sector)
        pickupConfig = pickupToPostcode.SECTOR[sector]?.own;
      else if (!postcode && !pickupConfig)
        pickupConfig = pickupToPostcode.SECTOR[sector]?.inherited;

      if (!postcode && !sector)
        pickupConfig = pickupToPostcode.OUTER[outer]?.own;
      else if (!pickupConfig)
        pickupConfig = pickupToPostcode.OUTER[outer]?.inherited;

      return (
        pickupConfig || {
          configByRouteCode: {},
          popupLabels: {},
          outerRouteCodes: [null],
          sectorRouteCodes: [],
          postcodeRouteCodes: [],
          routeCodes: [null],
        }
      );
    };

    const options = [];
    Object.keys(lookupTables.OUTER).forEach((outer) => {
      options.push({
        value: outer,
        type: OUTER,
        routeCode: routeOuter[outer]?.routeCode,
        collection:
          (planType === COLLECTIONS_PLAN_TYPE &&
            collectionAreas?.[OUTER]?.[outer]) ||
          {},
        overrides: overridesMap[outer],
        pickupConfig: getPostcodePickupConfig({ outer }),
      });
    });

    Object.keys(lookupTables.SECTOR).forEach((sector) => {
      const outer = Postcode.toOuter(sector);
      let routeCode = (routeSector[sector] || routeOuter[outer])?.routeCode;
      if (isRedirectionRouteCode(routeCode))
        routeCode = redirectConfig[routeCode][sector].routeCode;

      let collection = {};
      if (planType === COLLECTIONS_PLAN_TYPE) {
        collection = collectionAreas?.[SECTOR]?.[sector] || {};
        [PLANNED, SAME_DAY].forEach((collectionType) => {
          const { ROUTE_CODE_KEY, DEFAULT_ROUTE_CODE_KEY } =
            DEFAULT_COLLECTION_TYPES[collectionType];
          const defaultRouteCode = collection[DEFAULT_ROUTE_CODE_KEY];
          if (
            !collection[ROUTE_CODE_KEY] &&
            isRedirectionRouteCode(defaultRouteCode)
          )
            collection = {
              ...collection,
              [DEFAULT_ROUTE_CODE_KEY]:
                redirectConfig[defaultRouteCode][sector].routeCode,
            };
        });
      }

      options.push({
        value: sector,
        type: SECTOR,
        routeCode,
        collection,
        overrides: overridesMap[sector],
        pickupConfig: getPostcodePickupConfig({ outer, sector }),
      });
    });

    Object.keys(lookupTables.POSTCODE).forEach((postcode) => {
      const outer = Postcode.toOuter(postcode);
      const sector = Postcode.toSector(postcode);
      let routeCode = (
        routePostcode[postcode] ||
        routeSector[sector] ||
        routeOuter[outer]
      )?.routeCode;
      if (isRedirectionRouteCode(routeCode))
        routeCode = getRedirectForPostcode({
          redirectConfig,
          redirectionRouteCode: routeCode,
          postcode,
        });

      let collection = {};
      if (planType === COLLECTIONS_PLAN_TYPE) {
        collection = collectionAreas?.[POSTCODE]?.[postcode] || {};
        [PLANNED, SAME_DAY].forEach((collectionType) => {
          const { ROUTE_CODE_KEY, DEFAULT_ROUTE_CODE_KEY } =
            DEFAULT_COLLECTION_TYPES[collectionType];
          const defaultRouteCode = collection[DEFAULT_ROUTE_CODE_KEY];
          if (
            !collection[ROUTE_CODE_KEY] &&
            isRedirectionRouteCode(defaultRouteCode)
          )
            collection = {
              ...collection,
              [DEFAULT_ROUTE_CODE_KEY]: getRedirectForPostcode({
                redirectConfig,
                redirectionRouteCode: defaultRouteCode,
                postcode,
              }),
            };
        });
      }

      options.push({
        value: postcode,
        type: POSTCODE,
        routeCode,
        collection,
        overrides: overridesMap[postcode],
        pickupConfig: getPostcodePickupConfig({ outer, sector, postcode }),
      });
    });
    return options;
  }
);

export const locationOptionsSelector = createSelector(
  locationConfigSelector,
  (locationConfig) => {
    if (!locationConfig) return [];
    else
      return locationConfig.map(({ locationCode, name, routeCode }) => ({
        locationCode,
        routeCode,
        value: `${locationCode} - ${name}`,
      }));
  }
);

export const routeOptionsSelector = createSelector(
  routesForPlanTypeSelector,
  driversSelector,
  (routes, drivers) => {
    if (!routes) return [];
    return routes.map((route) => {
      const driver = drivers[route.driverCode];
      const driverName = driver?.driverName || NO_DRIVER;
      return {
        value: `${route.routeCode} - ${driverName}`,
        routeCode: route.routeCode,
      };
    });
  }
);
